// SellingHubButtons.jsx
import React, { useState, useEffect } from 'react';
import Modal from 'react-bootstrap/Modal';
import { useNavigate } from 'react-router-dom';
import {
  FaBoxOpen,
  FaBriefcase,
  FaTruckLoading,
  FaShippingFast,
  FaWarehouse,
  FaGlobeAmericas,
  FaTimes,
  FaClock
} from 'react-icons/fa';
import { isDropshippingServiceActive } from '../pages/dropshippingOutPlatforms';
import { canViewDropshippingSellerCenter, getCommercialCapabilities, getCurrentElakayRole } from './Rolle';
import { getSellCenterFeatureStatus, SELL_CENTER_FEATURES } from '../../utils/sellCenterStatus';
import './SellingHubButtons.css';
import { getElakayLanguageContext } from "./dataElakay";

let __elakayInfoSource;
const __setElakayInfoSource = (infoSource) => { if (infoSource) __elakayInfoSource = infoSource; };
// Current language source: the app-wide one (always up to date after a language switch), else
// the infoSource this module last rendered with.
const __elakayLanguageSource = () => getElakayLanguageContext?.() || __elakayInfoSource;
const __elakayText = (key, fallback) =>
  __elakayLanguageSource()?.laguangeElakay?.("frontElakayHardcodedText", __elakayLanguageSource().languageAPI)?.["sellingHubButtons"]?.[key] || fallback;

// AnimationSettings -> Sell Center controls each card's status
// (active/soon/coming_back) INDEPENDENTLY PER ROLE now -- server_elakay/
// elakay_models/AnimationSettings.js's `sellCenter.<key>.statusByRole.<role>`
// (Phase 2). `role` here is ALWAYS getCurrentElakayRole(infoSource) --
// RoleSwitchDropdown's selected role when one is active, otherwise the
// account's real role (see Rolle.js's own doc comment on that function) --
// never re-derived here, per spec §9/§10. getSellCenterFeatureStatus (utils/
// sellCenterStatus.js, mirrors the backend helper) owns the full fallback
// chain (per-role -> legacy flat status -> hardcoded Phase-1 default), so a
// missing/malformed infoSource.allSettings (API down, old data, unknown
// role) never crashes and never silently unlocks or hides every card.
const getSellCenterStatus = (sellCenterKey) => {
  const role = getCurrentElakayRole(__elakayInfoSource);
  return getSellCenterFeatureStatus(__elakayInfoSource?.allSettings, sellCenterKey, role);
};

const buttonsData = [
  {
    id: 'sell-product',
    sellCenterKey: 'sellProduct',
    get title() { return __elakayText("t007", "Sell Your Product"); },
    get subtitle() { return __elakayText("t008", "List physical items"); },
    icon: <FaBoxOpen />,
    link: 'sellProduct',
    get status() { return getSellCenterStatus('sellProduct'); }
  },
  {
    id: 'sell-service',
    sellCenterKey: 'sellService',
    get title() { return __elakayText("t009", "Sell My Service"); },
    get subtitle() { return __elakayText("t010", "Offer your expertise"); },
    icon: <FaBriefcase />,
    link: 'sellService',
    get status() { return getSellCenterStatus('sellService'); }
  },
  {
    id: 'drop-in',
    sellCenterKey: 'dropshippingIn',
    get title() { return __elakayText("t011", "Dropshipping In"); },
    get subtitle() { return __elakayText("t012", "Receive products"); },
    icon: <FaTruckLoading />,
    // Route dédiée (Dropshipping Interne Elakay, server_elakay/
    // dropshippingInterne) -- distincte de /seller/providers (Provider /
    // Producer In) même si les deux réutilisent les mêmes composants
    // ProviderSectionHeader/ProviderProductCard (spec §2 : pas de route en
    // double pour la même fonction, mais deux cartes -> deux points d'entrée).
    link: '/seller/dropshipping-in',
    get status() { return getSellCenterStatus('dropshippingIn'); }
  },
  {
    id: 'drop-out',
    sellCenterKey: 'dropshippingOut',
    get title() { return __elakayText("t013", "Dropshipping Out"); },
    get subtitle() { return __elakayText("t014", "Send to buyers"); },
    icon: <FaShippingFast />,
    link: '/dropshipping/platforms',
    // Sell Center's own status is the primary control for this card now;
    // dropshippingOut.enabled (a separate, broader "pause the whole
    // Dropshipping Out service" switch used elsewhere, e.g.
    // elakay_Dropshipping_out.jsx) can still force it into "soon" on top of
    // whatever Sell Center says, but never overrides an explicit "soon"/
    // "coming_back" choice made here with "active".
    get status() {
      const sellCenterStatus = getSellCenterStatus('dropshippingOut');
      if (sellCenterStatus !== 'active') return sellCenterStatus;
      return isDropshippingServiceActive(__elakayInfoSource?.allSettings?.dropshippingOut) ? 'active' : 'soon';
    }
  },
  {
    id: 'provider-in',
    sellCenterKey: 'providerProducerIn',
    get title() { return __elakayText("t015", "Provider / Producer In"); },
    get subtitle() { return __elakayText("t016", "Internal suppliers"); },
    icon: <FaWarehouse />,
    // Route déjà existante (Sell Center Phase 6/8, dropshippingInterne) --
    // réutilisée telle quelle plutôt qu'une nouvelle route dupliquée.
    link: '/seller/providers',
    get status() { return getSellCenterStatus('providerProducerIn'); }
  },
  {
    id: 'provider-out',
    sellCenterKey: 'providerProducerOut',
    get title() { return __elakayText("t017", "Provider / Producer Out"); },
    get subtitle() { return __elakayText("t018", "External suppliers"); },
    icon: <FaGlobeAmericas />,
    link: 'providerOut',
    get status() { return getSellCenterStatus('providerProducerOut'); }
  }
];

const providerText = (key, fallback) =>
  __elakayLanguageSource()?.laguangeElakay?.("frontElakayHardcodedText", __elakayLanguageSource().languageAPI)?.providerWorkspace?.[key] || fallback;

// Provider cards: one per SELL_CENTER_FEATURES entry of group "provider"
// (utils/sellCenterStatus.js -- route/capability/feature key live there, the
// same keys AnimationSettings uses). "provider-shop" is the only card outside
// the status system (always active).
const PROVIDER_CARD_UI = {
  addProviderProduct: { id: "provider-add", i18n: ["addProduct", "Add Provider Product"], hint: ["sourceHint", "Create a source product for Sellers"], icon: <FaBoxOpen /> },
  manageProviderProducts: { id: "provider-manage", i18n: ["manageProducts", "Manage Provider Products"], hint: ["inventoryHint", "Source inventory and availability"], icon: <FaWarehouse /> },
  wholesale: { id: "provider-wholesale", i18n: ["wholesale", "Wholesale"], hint: ["wholesaleHint", "Minimum quantities and tier prices"], icon: <FaBoxOpen /> },
  providerDropshipping: { id: "provider-dropshipping", i18n: ["dropshipping", "Provider Dropshipping"], hint: ["dropshippingHint", "Manage source products available to Sellers"], icon: <FaTruckLoading /> },
  providerFulfillment: { id: "provider-fulfillment", i18n: ["fulfillment", "Fulfillment settings"], hint: ["fulfillmentHint", "Shipping and fulfillment by product"], icon: <FaShippingFast /> },
  providerOrders: { id: "provider-orders", i18n: ["orders", "Provider orders"], hint: ["ordersHint", "Provider order processing"], icon: <FaBriefcase /> },
};

const providerButtons = (caps) => [
  // Provider storefront (Provider Center > My Shop) -- never the Seller My Shop (/myShop).
  { id: "provider-shop", title: providerText("myShop", "My Shop"), subtitle: providerText("shopHint", "Manage your shop"), icon: <FaWarehouse />, link: "/provider/my-shop", status: "active" },
  ...SELL_CENTER_FEATURES
    .filter((feature) => feature.group === "provider" && PROVIDER_CARD_UI[feature.key])
    // Permission first: the status below never grants a capability the
    // effective role doesn't have.
    .filter((feature) => !feature.capability || caps[feature.capability])
    .map((feature) => {
      const ui = PROVIDER_CARD_UI[feature.key];
      return {
        id: ui.id,
        sellCenterKey: feature.key,
        title: providerText(...ui.i18n),
        subtitle: providerText(...ui.hint),
        icon: ui.icon,
        link: feature.route,
        status: getSellCenterStatus(feature.key),
      };
    }),
];

const AUTO_CLOSE_DURATION = 5000;

const ComingSoonModal = ({ feature, onClose }) => {
  const [secondsLeft, setSecondsLeft] = useState(5);

  useEffect(() => {
    if (!feature) return;
    setSecondsLeft(5);

    const closeTimer = setTimeout(onClose, AUTO_CLOSE_DURATION);
    const tickTimer = setInterval(() => {
      setSecondsLeft((prev) => (prev > 1 ? prev - 1 : 0));
    }, 1000);

    return () => {
      clearTimeout(closeTimer);
      clearInterval(tickTimer);
    };
  }, [feature, onClose]);

  return (
    <Modal
      show={!!feature}
      onHide={onClose}
      centered
      contentClassName="coming-soon-modal"
      backdropClassName="coming-soon-backdrop"
      aria-labelledby="coming-soon-title"
    >
      <Modal.Body>
        <button
          className="coming-soon-close"
          onClick={onClose}
          aria-label={__elakayText("t001", "Close modal")}
        >
          <FaTimes />
        </button>

        <div className="coming-soon-icon">
          <FaClock />
        </div>

        <h2 id="coming-soon-title" className="coming-soon-title">
          {feature?.status === 'coming_back'
            ? __elakayText("t020", "Coming Back")
            : __elakayText("t003", "Coming Soon")}
        </h2>

        <p className="coming-soon-feature">{feature?.title}</p>

        <p className="coming-soon-message">
          {feature?.status === 'coming_back'
            ? __elakayText("t019", "This feature was available before and is temporarily unavailable. It will be back soon!")
            : __elakayText("t004", "This feature is currently under development. Stay tuned — we'll let you know as soon as it's ready!")}
        </p>

        <p className="coming-soon-countdown">{__elakayText("t005", "Closing in")}<strong>{secondsLeft}s</strong>
        </p>

        <div className="coming-soon-progress">
          <div className="coming-soon-progress-bar" />
        </div>
      </Modal.Body>
    </Modal>
  );
};

const SellingHubButtons = ({ onNavigate, infoSource }) => {
  __setElakayInfoSource(infoSource);
  const navigate = useNavigate();
  const [inactiveFeature, setInactiveFeature] = useState(null);
  const canSeeDropshipping = canViewDropshippingSellerCenter(infoSource);
  // Cards are built from the EFFECTIVE role's capabilities (Rolle.js), never
  // from role-name comparisons: Seller -> Seller cards, Provider -> Provider
  // cards, Associate..Family (RoleSwitch "All"/own role) -> both, in two
  // labelled sections. Selecting Seller/Provider in RoleSwitchDropdown narrows
  // a combined account to that single mode.
  const caps = getCommercialCapabilities(infoSource);
  const canBrowse = caps.canBrowseProviders && canSeeDropshipping;
  const sellerCards = !caps.canAccessSellerCenter ? [] : buttonsData
    .filter((button) => button.id !== "sell-product" || caps.canCreateSellerProduct)
    .filter((button) => !["drop-in", "drop-out", "provider-in"].includes(button.id) || canBrowse)
    .map((button) => ({ ...button, status: ["sell-service", "provider-out"].includes(button.id) && button.status === "active" ? "soon" : button.status }));
  const isCombined = caps.canAccessCombinedSellCenter;
  // The Provider storefront card is shown to combined roles too: it opens the Provider My Shop, a
  // different page from the Seller My Shop this hub lives in.
  const providerCards = !caps.canAccessProviderCenter ? [] : providerButtons(caps);
  const sections = isCombined
    ? [
        { id: "seller", title: __elakayText("t021", "Seller Activities"), cards: sellerCards },
        { id: "provider", title: __elakayText("t022", "Provider / Producer Activities"), cards: providerCards },
      ]
    : [{ id: "single", title: null, cards: caps.canAccessProviderCenter ? providerCards : sellerCards }];

  // Sell Center status (AnimationSettings): "active" is the only status that
  // ever navigates. "soon"/"coming_back" both stay visible but never
  // navigate -- they only ever open the modal below, whether triggered by a
  // mouse click or a keyboard Enter/Space on this <button> (same onClick for
  // both, so there's no separate keyboard path that could bypass this gate).
  const handleClick = (btn) => {
    if (btn.status !== 'active') {
      setInactiveFeature(btn);
      return;
    }

    if (btn.link.startsWith('/')) {
      navigate(btn.link);
      return;
    }

    onNavigate?.(btn.link);
  };

  return (
    <>
      <div className="selling-hub-container">
        {sections.map((section) => (
          <section key={section.id} className="selling-hub-section" aria-label={section.title || undefined}>
            {section.title && <h3 className="selling-hub-section-title">{section.title}</h3>}
            <div className="selling-hub-grid">
              {section.cards.map((btn) => {
                const isActive = btn.status === 'active';
                const classes = [
                  'hub-button',
                  !isActive && 'is-inactive'
                ].filter(Boolean).join(' ');

                return (
                  <button
                    key={btn.id}
                    className={classes}
                    onClick={() => handleClick(btn)}
                    aria-label={btn.title}
                    aria-disabled={!isActive}
                    data-page-link={btn.link}
                  >
                    {!isActive && (
                      <span className="hub-button-badge">
                        {btn.status === 'coming_back'
                          ? __elakayText("t020", "Coming Back")
                          : __elakayText("t002", "Soon")}
                      </span>
                    )}
                    <div className="hub-button-icon">{btn.icon}</div>
                    <div className="hub-button-text">
                      <span className="hub-button-title">{btn.title}</span>
                      <span className="hub-button-subtitle">{btn.subtitle}</span>
                    </div>
                  </button>
                );
              })}
            </div>
          </section>
        ))}
      </div>

      <ComingSoonModal
        feature={inactiveFeature}
        onClose={() => setInactiveFeature(null)}
      />
    </>
  );
};

export default SellingHubButtons;
